import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  MapPin,
  Phone,
  Mail,
  Heart,
} from "lucide-react";
import { coursesData } from "@/data/courses";

const Footer = () => {
  return (
    <footer className="relative bg-secondary text-secondary-foreground border-t border-border/10">
      <div className="max-w-6xl mx-auto px-4 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">

          {/* About Section */}
          <div className="lg:col-span-3 space-y-6">
            <h3 className="text-3xl font-black text-white tracking-tight">
              CODO<span className="text-codo-green">.</span>
            </h3>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm">
              Empowering 100,000 digital-first careers. We build job-ready tech skills without the academic noise. Code the future.
            </p>

            {/* Download Buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href="#"
                aria-label="Download on the App Store"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 transition-all hover:bg-white/10 hover:border-codo-green hover:scale-105"
              >
                <svg viewBox="0 0 384 512" className="w-5 h-5 fill-white">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 21.8-88.5 21.8-11.4 0-51.1-20.8-83.6-20.1-42.9.6-82.7 25.4-104.7 63.6-44 75.7-11.3 189.3 31.3 251.2 21.3 30.6 46.2 65 79 64.1 31.2-1.2 43.1-20.2 81.3-20.2 37.5 0 49.3 20.2 81.9 19.6 33.4-.6 55.4-30.6 76.5-61.2 24.3-35.1 34.3-69.1 34.7-71.1-1.3-.6-66.2-25.2-66.6-100.8zM266.1 82c23-28.7 18.2-61.3 7.8-82 0 0-4.6-.3-12 .6-27.1 3.2-51.1 20.3-64.4 41.5-16.1 24.6-12.8 54.5-9.8 61.3 2.8 6.4 12 11.2 12 11.2s43.4-3.9 66.4-32.6z" />
                </svg>
                <div className="text-left">
                  <p className="text-[9px] text-white/60 leading-none">Get it on</p>
                  <p className="text-xs font-semibold text-white leading-tight">App Store</p>
                </div>
              </a>
              <a
                href="#"
                aria-label="Get it on Google Play"
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 transition-all hover:bg-white/10 hover:border-codo-green hover:scale-105"
              >
                <svg viewBox="0 0 512 512" className="w-5 h-5 fill-white">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                </svg>
                <div className="text-left">
                  <p className="text-[9px] text-white/60 leading-none">GET IT ON</p>
                  <p className="text-xs font-semibold text-white leading-tight">Google Play</p>
                </div>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2 space-y-6">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Company</h4>
            <ul className="space-y-4">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About" },
                { to: "/careers", label: "Careers" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    state={{ scrollFromFooter: true }}
                    className="text-sm text-white/70 hover:text-codo-green transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies */}
          <div className="lg:col-span-2 space-y-6">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Policies</h4>
            <ul className="space-y-4">
              {[
                { to: "/terms-of-use", label: "Terms of Use" },
                { to: "/privacy-policy", label: "Privacy Policy" },
                { to: "/refund-policy", label: "Refund Policy" },
                { to: "/shipping-policy", label: "Shipping Policy" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/70 hover:text-codo-green transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Courses */}
          <div className="lg:col-span-2 space-y-6">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Programs</h4>
            <ul className="space-y-4">
              {coursesData.slice(0, 4).map((course) => (
                <li key={course.slug}>
                  <Link
                    to={`/courses/${course.slug}`}
                    state={{ scrollFromFooter: true }}
                    className="text-sm text-white/70 hover:text-codo-green transition-colors duration-200 line-clamp-1"
                  >
                    {course.title.replace(" Bootcamp: Full-Stack JavaScript", "").replace(" Bootcamp", "").replace(" & Development", "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-3 space-y-6">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Contact</h4>
            <div className="space-y-5">
              <a href="tel:+918086995559" className="flex items-center gap-3 text-sm text-white/70 hover:text-white transition-colors">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-codo-green" />
                </div>
                +91 808 699 5559
              </a>
              <a href="mailto:info@codoacademy.com" className="flex items-center gap-3 text-sm text-white/70 hover:text-white transition-colors">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-codo-green" />
                </div>
                info@codoacademy.com
              </a>
              <div className="flex items-start gap-3 text-sm text-white/70 hover:text-white transition-colors">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-codo-green" />
                </div>
                <span className="leading-relaxed mt-1.5">
                  Kottakkal, Kerala
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom Row (Socials & Copyright) */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-xs text-white/70 order-2 md:order-1">
            © {new Date().getFullYear()} CODO Academy. All Rights Reserved.
          </p>

          <div className="flex gap-4 order-1 md:order-2">
            {[
              { Icon: Twitter, href: "https://x.com/codoacademy", label: "Twitter" },
              { Icon: Instagram, href: "https://www.instagram.com/codo.ai/", label: "Instagram" },
              { Icon: Facebook, href: "https://www.facebook.com/codoaiinnovations", label: "Facebook" },
              { Icon: Linkedin, href: "https://share.google/wmFEaCPNm9dixr94Y", label: "LinkedIn" },
              { Icon: Youtube, href: "https://www.youtube.com/@codoai", label: "YouTube" },
            ].map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="text-white/70 hover:text-codo-green transition-all duration-300 hover:scale-110"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
              >
                <item.Icon className="w-5 h-5" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-white/70 order-3">
            Made with <Heart className="w-3 h-3 text-red-500 animate-pulse" /> for the future
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
